import {
  createEffect,
  createSignal,
  onMount,
  Show,
  type Component,
} from "solid-js";
import { useNavigate, useParams } from "@solidjs/router";
import { FileText, Save, Eye, Pencil } from "lucide-solid";
import { api, fetchBlob } from "../lib/api";
import { isLoggedIn } from "../lib/auth";
import Markdown from "../components/Markdown";
import { TextField, TextFieldLabel, TextFieldInput } from "../components/ui/text-field";
import { Button } from "../components/ui/Button";

const STARTER = `# Başlık

Buraya markdown ile içeriğini yaz...

\`\`\`js
console.log("merhaba snippland");
\`\`\`
`;

const SnipEditor: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const editId = () => (params.id ? Number(params.id) : undefined);

  const [title, setTitle] = createSignal("");
  const [summary, setSummary] = createSignal("");
  const [content, setContent] = createSignal(STARTER);
  const [tab, setTab] = createSignal<"write" | "preview">("write");
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Redirect anonymous users away from the editor.
  createEffect(() => {
    if (!isLoggedIn()) navigate("/sign-in", { replace: true });
  });

  // Load existing snip content when editing.
  onMount(async () => {
    const id = editId();
    if (id === undefined) return;
    const { data, error } = await api.GET("/snips/{id}", {
      params: { path: { id } },
    });
    if (error || !data) return;
    setTitle(data.title ?? "");
    setSummary(data.summary ?? "");
    const blobId = data.files?.[0]?.blobId;
    if (blobId) setContent(await fetchBlob(blobId));
  });

  const save = async (e: Event) => {
    e.preventDefault();
    if (!title().trim()) {
      setError("Başlık zorunludur");
      return;
    }
    setError(null);
    setSaving(true);
    const body = {
      title: title().trim(),
      summary: summary().trim(),
      format: "article",
      files: [{ filename: "index.md", content: content() }],
    };
    try {
      const id = editId();
      if (id === undefined) {
        const { data, error } = await api.POST("/snips", { body });
        if (error || !data) throw new Error("Kaydedilemedi");
        navigate(`/snip/${data.id}`);
      } else {
        const { data, error } = await api.PUT("/snips/{id}", {
          params: { path: { id } },
          body,
        });
        if (error || !data) throw new Error("Güncellenemedi");
        navigate(`/snip/${data.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} class="mx-auto max-w-3xl px-4 py-6">
      <div class="mb-5 flex items-center gap-2">
        <FileText size={20} class="text-primary" />
        <h1 class="m-0 text-lg font-semibold">
          {editId() === undefined ? "Yeni Makale" : "Makaleyi Düzenle"}
        </h1>
      </div>

      <div class="flex flex-col gap-4">
        <TextField value={title()} onChange={setTitle} required>
          <TextFieldLabel>Başlık</TextFieldLabel>
          <TextFieldInput type="text" placeholder="Makale başlığı" />
        </TextField>
        <TextField value={summary()} onChange={setSummary}>
          <TextFieldLabel>Özet</TextFieldLabel>
          <TextFieldInput type="text" placeholder="Kısa açıklama (opsiyonel)" />
        </TextField>

        <div>
          <div class="mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTab("write")}
              class={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${tab() === "write" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
            >
              <Pencil size={14} /> Yaz
            </button>
            <button
              type="button"
              onClick={() => setTab("preview")}
              class={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${tab() === "preview" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
            >
              <Eye size={14} /> Önizleme
            </button>
          </div>

          <Show
            when={tab() === "write"}
            fallback={
              <div class="min-h-[20rem] rounded-md border border-border bg-background p-4">
                <Markdown source={content()} />
              </div>
            }
          >
            <textarea
              value={content()}
              onInput={(e) => setContent(e.currentTarget.value)}
              rows={18}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
            />
          </Show>
        </div>

        <Show when={error()}>
          <p class="m-0 text-sm text-destructive">{error()}</p>
        </Show>

        <div class="flex items-center gap-3">
          <Button type="submit" disabled={saving()}>
            <Save size={16} />
            {saving() ? "Kaydediliyor..." : "Yayınla"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            İptal
          </Button>
        </div>
      </div>
    </form>
  );
};

export default SnipEditor;
